import {
  getItemsByCategories,
  getGalleryTabFor3DViews,
  hasVideoElement,
  hasVirtualViewsElement,
} from "../SectionFeatured.tsx/utils";
import classes from "./ActionButtonSection.module.scss";
import Skeleton from "@Components/Skeleton";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import { AppState } from "@Store/index";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import CubeIcon from "@Icons/CubeIcon";
import FloorPlanIcon from "@Icons/FloorPlanIcon";
import MapIcon from "@Icons/MapIcon";
import PhotoIcon from "@Icons/PhotoIcon";
import VideoPlayIcon from "@Icons/VideoPlayIcon";
import { hasFloorPlanData } from "../FloorPlansSection/utils/floorPlanHelpers";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { transformVideoPath } from "../ReviewsSection/data/helper";
import StarRating from "@Components/StarRating";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import config from "@Config/index";
//

const ActionButton = ({ show = true, text, onClickActionButton, icon, isDesktop = false }: any) => {
  const { t } = useTranslationHook();
  const handleButtonClick = (e: any) => {
    onClickActionButton(e, text === "Map View");

    gtagEventEmitter({
      action: `clicked_${text?.replace(" ", "_")}_tag`,
      category: PAGE.INVENTORY,
      label: text,
    });
  };

  return (
    show && (
      <button
        data-testid={text}
        type="button"
        onClick={handleButtonClick}
        className={formatClassNames(classes.actionButton, isDesktop && classes.desktop)}
      >
        {isDesktop && <>{icon}</>}
        {t(text)}
      </button>
    )
  );
};

const ActionButtonSection = ({
  isLoading,
  isDesktop = false,
}: {
  isLoading: boolean;
  isDesktop?: boolean;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const { data, reviewData, reviews_count, reviews_rating, roomTypesData } = useSelector(
    ({ propertyPageData }: AppState) => ({
      data: propertyPageData?.data,
      reviewData: propertyPageData.reviewData?.result,
      reviews_count: propertyPageData?.inventoryReviewData?.length,
      reviews_rating: propertyPageData.data?.reviews_rating,
      roomTypesData: propertyPageData.roomTypes?.data || [],
    }),
    shallowEqual,
  );
  const isReviewsSectionV2Enabled =
    (useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.REVIEWS_SECTION_V2) || config.IS_CHINA_APP) &&
    data?.meta?.review_summary?.summary;
  const isFloorPlansSectionEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_FLOOR_PLANS_SECTION) || config.IS_CHINA_APP;
  const actionButtons = [
    {
      text: t("propertypage.components.actionbuttonsection.photos"),
      icon: <PhotoIcon />,
      tab: "all",
      show: true,
    },
    {
      text: t("propertypage.components.actionbuttonsection.videos"),
      show: Boolean(hasVideoElement(data)),
      icon: <VideoPlayIcon />,
      tab: "videos",
    },
    {
      text: t("propertypage.components.actionbuttonsection.views"),
      show: Boolean(hasVirtualViewsElement(data)),
      icon: <CubeIcon />,
      tab: "3D",
    },
    {
      text: t("propertypage.components.actionbuttonsection.mapview"),
      icon: <MapIcon />,
      show: true,
    },
    {
      text: t("propertypage.components.actionbuttonsection.floorplan"),
      icon: <FloorPlanIcon size="18" />,
      show:
        isFloorPlansSectionEnabled &&
        (hasFloorPlanData(data) ||
          roomTypesData?.some((child: any) =>
            child.images?.some((img: any) => img.type === "floor_plan"),
          )),
      tab: "floor_plan",
    },
  ];

  const openGallery = (tab: any) => {
    const galleryTab = tab === "3D" ? getGalleryTabFor3DViews(data, roomTypesData) : tab;
    const filteredReview =
      reviewData?.flatMap(({ media, rating, user_details }: any) =>
        (media || []).map(({ path, thumbnail_url }: any) => ({
          path: transformVideoPath(path),
          base_path: transformVideoPath(path),
          platform: "gumlet",
          category: thumbnail_url ? "videos" : "images",
          thumbnail_url: thumbnail_url ? transformVideoPath(thumbnail_url) : null,
          user_details,
          rating,
          showCaption: true,
        })),
      ) || [];
    const filteredSitePlan =
      data?.site_plan?.map(
        ({ building, number_of_floors, floor_details: [...floor_details_temp] }: any) => ({
          building,
          number_of_floors,
          floor_details: floor_details_temp?.sort(
            (a: any, b: any) => parseFloat(a?.floor) - parseFloat(b?.floor),
          ),
        }),
      ) || [];

    const additionData: any = {};
    if (data?.site_plan?.length > 0) {
      additionData["site_plan"] = filteredSitePlan;
    }
    if (filteredReview.length > 0) {
      additionData.shot_by_residents = filteredReview;
    }
    const _galleryItems = { ...getItemsByCategories(data), ...(additionData || {}) };
    const { floor_plan, site_plan, ...galleryItems } = _galleryItems;
    if (floor_plan) galleryItems["floor_plan"] = floor_plan;
    if (site_plan) galleryItems["site_plan"] = site_plan;
    propertyPageDispatcher.showGalleryModal({
      show: true,
      selectedTab: galleryTab,
      data: galleryItems,
    });
  };

  const onClickActionButton = (isMapView: boolean, tab: any) => {
    if (isMapView) {
      const element = document.getElementById("map");
      if (!element) return;
      const headerOffset = 54 + 48; // padding plus navbar height
      const elementPosition = element?.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    } else if (tab === "floor_plan") {
      // Delegate to SectionNavigation, which scrolls with the correct offset and
      // re-corrects after layout shifts settle (same path the nav bar uses).
      // Doing our own scroll here lands short (at the reviews section) because it
      // doesn't account for the sticky nav / lazy sections shifting layout.
      const dispatchNavigate = () =>
        window.dispatchEvent(
          new CustomEvent("navigate_to_section", { detail: { id: "floor-layout" } }),
        );
      if (document.getElementById("floor-layout")) {
        dispatchNavigate();
      } else {
        setTimeout(dispatchNavigate, 300);
      }
    } else {
      openGallery(tab);
    }
  };
  const redirectToReviewSection = () => {
    // When ReviewsSectionV2 is enabled, open the reviews panel directly on desktop
    if (isReviewsSectionV2Enabled && isDesktop && typeof window !== "undefined") {
      window.dispatchEvent(new Event("open-reviews-panel"));
      return;
    }
    const element = document.getElementById("reviews");
    if (!element) return;
    const headerOffset = 54 + 48; // padding plus navbar height
    const elementPosition = element?.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const showRatings = Boolean(reviews_rating >= "4.0"); // P1D-3500
  return (
    <div
      data-testid="mobile-Action-Button-Section"
      className={formatClassNames(classes.container, isDesktop && classes.desktop)}
      style={{
        gridTemplateColumns: isDesktop
          ? "unset"
          : `repeat(${actionButtons.filter((it) => it.show).length}, 1fr)`,
      }}
    >
      <div
        className={formatClassNames(classes.container, isDesktop && classes.desktop)}
        style={{
          gridTemplateColumns: isDesktop
            ? "unset"
            : `repeat(${actionButtons.filter((it) => it.show).length}, 1fr)`,
          padding: 0,
        }}
      >
        {actionButtons.map(({ text, show, tab, icon }) =>
          isLoading ? (
            <>{!!show && <Skeleton width="100%" height="36px" addBorderRadius key={text} />}</>
          ) : (
            <ActionButton
              data-testid={text}
              onClickActionButton={() =>
                onClickActionButton(
                  text === t("propertypage.components.actionbuttonsection.mapview"),
                  tab,
                )
              }
              key={text}
              text={text}
              show={show}
              icon={icon}
              isDesktop={isDesktop}
            />
          ),
        )}
      </div>
      <div
        onClick={showRatings && isDesktop ? redirectToReviewSection : undefined}
        role="none"
        className={classes.addUnderline}
      >
        {showRatings && isDesktop && (
          <StarRating
            rating={reviews_rating}
            reviewCount={reviews_count || 0}
            className={formatClassNames(classes.trustPilotPill, classes.desktop)}
            testId="searchPage-inventoryCard-trustpilotPill"
          />
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(ActionButtonSection, {
  componentName: "ActionButtonSection-PropertyPage",
  showForChina: true,
});
